import { CloseIcon, InfoOutlinedIcon } from '@getgo/chameleon-icons/react';
import { Alert, Button, Typography } from '@getgo/chameleon-web-react-wrapper/register';
import { ColumnFiltersState, PaginationState, RowSelectionState, SortingState, Updater } from '@tanstack/react-table';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useSwr from 'swr';
import GoToResolveAppLink from '../../../core/components/GoToResolveAppLink';
import { TrackedButton } from '../../../core/components/tracking/TrackedComponents';
import Tenant from '../../../core/models/Tenant';
import { PatchViewLoadedEvent, PatchViewUpdatesSelectedEvent } from '../../../core/models/UserTrackingEvents';
import logger from '../../../core/services/logger.service';
import { navigate } from '../../../core/services/navigation.service';
import trackingService from '../../../core/services/tracking/tracking.service';
import { useAppDispatch, useAppSelector } from '../../../core/store/root.store';
import { selectNavigationRoute } from '../../../ui-frame/state/ui-frame.slice';
import useGetGlobalSelectedTenants from '../../../windows-updates/hooks/useGetGlobalSelectedTenants';
import useGetTenantDevicesAndGroups from '../../../windows-updates/hooks/useGetTenantDevicesAndGroups';
import useTenantDeviceGroupIds from '../../../windows-updates/hooks/useTenantDeviceGroupIds';
import useCrossLinkInfoBoxStore from '../../hooks/useCrossLinkInfoBoxStore';
import useFirstVisitFlag from '../../hooks/useFirstVisitFlag';
import useCommonDeviceDataService from '../../services/common-device-data-service/useCommonDeviceDataService';
import { PatchviewDataService } from '../../services/PatchviewDataService';
import usePatchviewDataService from '../../services/usePatchviewDataService';
import {
  addSelectedUpdates,
  selectPagination,
  selectRowSelection,
  selectSearchText,
  selectSelectedUpdates,
  selectShowSecurityUpdatesOnly,
  selectSorting,
  setLocalSelectedTenants,
  setPagination,
  setRowSelection,
  setSearchText,
  setSelectedUpdates,
  setShowSecurityUpdatesOnly,
  setSorting
} from '../../state/windows-updates-selector-slice';
import { UpdateRow } from '../../types/frontend/update-row';
import { ErrorFallback } from '../error-fallback/ErrorFallback';
import { NoUpdates } from '../no-updates/NoUpdates';
import { UpToDate } from '../up-to-date/UpToDate';
import WindowsUpdatesGrid from './windows-updates-grid/WindowsUpdatesGrid';
import WindowsUpdatesFilterHeader from './WindowsUpdatesFilterHeader';
import WindowsUpdatesFilterPanel from './WindowsUpdatesFilterPanel';
import styles from './WindowsUpdatesSelector.module.scss';

function WindowsUpdatesSelector(): ReactElement {
  const { t } = useTranslation();
  const { patchViewDataService, isPatchviewDataServiceReady } = usePatchviewDataService();
  const { commonDeviceDataService, isCommonDeviceDataServiceReady } = useCommonDeviceDataService();

  const [searchParams] = useSearchParams();
  const params = searchParams.get('params');

  const dispatch = useAppDispatch();
  const sorting = useAppSelector(selectSorting);
  const selectedUpdates = useAppSelector(selectSelectedUpdates);
  const rowSelection = useAppSelector(selectRowSelection);
  const searchText = useAppSelector(selectSearchText);
  const pagination = useAppSelector(selectPagination);
  const showSecurityUpdatesOnly = useAppSelector(selectShowSecurityUpdatesOnly);
  const [hasVisitedPatchviewBefore, setHasVisitedPatchviewBefore] = useFirstVisitFlag();
  const navigationRoute = useAppSelector(selectNavigationRoute);
  const [urlDeviceIds, setUrlDeviceIds] = useState<string[]>([]);

  const { crossLinkInfoBoxStore, setCrossLinkInfoBoxStore } = useCrossLinkInfoBoxStore();

  useEffect(() => {
    if (!crossLinkInfoBoxStore.visible && crossLinkInfoBoxStore.lastCloseDate) {
      const now = new Date().valueOf();
      const lastCloseDate = new Date(crossLinkInfoBoxStore.lastCloseDate).valueOf();
      if (now - lastCloseDate > 1000 * 60 * 60 * 24 * 7 && crossLinkInfoBoxStore.closedTimes < 3) {
        setCrossLinkInfoBoxStore({ ...crossLinkInfoBoxStore, visible: true });
      }
    }
  }, [crossLinkInfoBoxStore, setCrossLinkInfoBoxStore]);

  const globalTenants = useGetGlobalSelectedTenants();
  const tenantDevicesAndGroups = useGetTenantDevicesAndGroups();
  const tenantDeviceGroupIds = useTenantDeviceGroupIds(tenantDevicesAndGroups, 'osUpdate');
  const tenantsAutocompleteRef = useRef(null);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    ...(searchParams.get('title') ? [{ id: 'title', value: searchParams.getAll('title') }] : [])
  ]);

  useEffect(() => {
    if (params) {
      navigate({ path: '/' });
      const parsedParams = JSON.parse(params ? params : '');
      setUrlDeviceIds(parsedParams.deviceIds || []);
    }
  }, [params]);

  useEffect(() => {
    try {
      trackingService.trackUserEvent(new PatchViewLoadedEvent());
    } catch (error) {
      logger.logError(error as Error | string);
    }
  }, []);

  useEffect(() => {
    const path = showSecurityUpdatesOnly
      ? ('/windows-updates#security-only' as '/windows-updates')
      : ('/windows-updates#all-updates' as '/windows-updates');
    if (navigationRoute.includes('/windows-updates')) {
      navigate({ path });
    }
  }, [navigationRoute, showSecurityUpdatesOnly]);

  useEffect(() => {
    if (navigationRoute.includes('/windows-updates#security-only')) {
      dispatch(setShowSecurityUpdatesOnly(true));
    }
    if (navigationRoute.includes('/windows-updates#all-updates')) {
      dispatch(setShowSecurityUpdatesOnly(false));
    }
  }, [dispatch, navigationRoute]);

  const fetchAvailableUpdates = async (patchViewDataService: PatchviewDataService): Promise<UpdateRow[]> => {
    if (!patchViewDataService) {
      throw new Error('PatchviewDataService is not initialized');
    }
    const { availableUpdates } = await patchViewDataService.getAvailableUpdates({
      pagination,
      sorting,
      securityUpdatesOnly: showSecurityUpdatesOnly,
      searchText,
      deviceIds: urlDeviceIds,
      deviceGroupIds: tenantDeviceGroupIds
    });
    return availableUpdates;
  };

  const fetchUpdatesCount = async (patchViewDataService: PatchviewDataService): Promise<number> => {
    if (!patchViewDataService) {
      throw new Error('PatchviewDataService is not initialized');
    }
    return patchViewDataService.getTotalAvailableUpdatesCount({
      securityUpdatesOnly: showSecurityUpdatesOnly,
      searchText,
      deviceIds: urlDeviceIds,
      deviceGroupIds: tenantDeviceGroupIds
    });
  };

  const {
    data: updates,
    error: updatesError,
    isLoading: updatesIsLoading
  } = useSwr(
    isPatchviewDataServiceReady
      ? [
          'getAvailableUpdates',
          pagination.pageIndex,
          pagination.pageSize,
          sorting,
          showSecurityUpdatesOnly,
          searchText,
          urlDeviceIds,
          tenantDeviceGroupIds
        ]
      : null,
    () => fetchAvailableUpdates(patchViewDataService),
    { shouldRetryOnError: false, use: [], keepPreviousData: true }
  );

  const { data: updatesCount, error: UpdatesCountError } = useSwr(
    isPatchviewDataServiceReady
      ? ['getAvailableUpdates', showSecurityUpdatesOnly, searchText, urlDeviceIds, tenantDeviceGroupIds]
      : null,
    () => fetchUpdatesCount(patchViewDataService),
    { shouldRetryOnError: false, use: [], keepPreviousData: true }
  );

  const {
    data: deviceIds,
    error: cdsError,
    isLoading: cdsIsLoading
  } = useSwr(isCommonDeviceDataServiceReady ? ['getDeviceIds'] : null, () => commonDeviceDataService.getDeviceIds(), {
    shouldRetryOnError: false,
    use: []
  });

  const handlePaginationChange = (updaterOrValue: Updater<PaginationState>): void => {
    if (typeof updaterOrValue === 'function') {
      const updated = updaterOrValue(pagination);
      dispatch(setPagination(updated));
    } else {
      setPagination(updaterOrValue);
    }
  };

  const handleRowSelectionChange = (updaterOrValue: Updater<RowSelectionState>): void => {
    if (typeof updaterOrValue !== 'function') return;

    const diff = updaterOrValue({});
    const updated = updaterOrValue(rowSelection);

    const hasNewKeys = !!Object.keys(diff).length;

    if (hasNewKeys) {
      handleNewUpdateKey(updated);
    } else {
      handleRemovedKey(updated);
    }

    dispatch(setRowSelection(updated));
  };

  const handleNewUpdateKey = (updated: RowSelectionState): void => {
    const updateKeys = Object.keys(updated);

    const newUpdateKeys = updateKeys.filter(
      update => !selectedUpdates.some(selectedUpdate => selectedUpdate.uuid === update)
    );

    const newSelectedUpdates = newUpdateKeys
      .map(newUpdateKey => updates?.find(update => update.uuid === newUpdateKey))
      .filter(Boolean) as UpdateRow[];

    dispatch(addSelectedUpdates(newSelectedUpdates));
  };

  const handleRemovedKey = (updated: RowSelectionState): void => {
    const updatedKeys = Object.keys(updated);
    const removedKeys = Object.keys(rowSelection).filter(key => !updatedKeys.includes(key));
    const updatedSelectedUpdates = selectedUpdates.filter(update => !removedKeys.includes(update.uuid));
    if (updatedSelectedUpdates) {
      dispatch(setSelectedUpdates(updatedSelectedUpdates));
    }
  };

  const handleSortingChange = (updaterOrValue: Updater<SortingState>): void => {
    if (typeof updaterOrValue === 'function') {
      dispatch(setSorting(updaterOrValue(sorting)));
    } else {
      setSorting(updaterOrValue);
    }
  };

  const handleShowSecurityUpdatesFilterChange = (showSecurityUpdatesOnly: boolean): void => {
    dispatch(setShowSecurityUpdatesOnly(showSecurityUpdatesOnly));
    dispatch(setPagination({ ...pagination, pageIndex: 0 }));
  };

  const handleTenantFilterChange = (tenantfilters: Tenant[]): void => {
    dispatch(setLocalSelectedTenants(tenantfilters));
    dispatch(setPagination({ ...pagination, pageIndex: 0 }));
  };

  const handleDeselectAll = (): void => {
    dispatch(setRowSelection({}));
    dispatch(setSelectedUpdates([]));
  };

  const pageCount = (): number | undefined => {
    if (updatesCount) {
      return Math.ceil(updatesCount / pagination.pageSize);
    }
  };

  const isUpToDateAndHasDevicesWithNoFilters =
    deviceIds?.length &&
    !updatesError &&
    !updatesIsLoading &&
    !updates?.length &&
    !showSecurityUpdatesOnly &&
    !searchText &&
    columnFilters.length === 0;

  const showAsLoading = cdsIsLoading || updatesIsLoading || !updates;

  if (deviceIds?.length === 0) {
    return <NoUpdates></NoUpdates>;
  }

  if (isUpToDateAndHasDevicesWithNoFilters) {
    if (!hasVisitedPatchviewBefore) {
      setHasVisitedPatchviewBefore(true);
      return <NoUpdates hasDevices={true}></NoUpdates>;
    }
    return <UpToDate></UpToDate>;
  }

  if (cdsError || updatesError || UpdatesCountError) {
    const error = updatesError ?? UpdatesCountError ?? cdsError;
    logger.logError(error);

    return <ErrorFallback></ErrorFallback>;
  }

  const renderDeselectButton = (): ReactElement | undefined => {
    if (selectedUpdates.length > 0) {
      return (
        <div>
          <Button variant="tertiary" onClick={handleDeselectAll} trailingIcon={<CloseIcon />}>
            {t('windowsUpdatesSelector.updatesSelected', { selectedUpdates: selectedUpdates.length })}
          </Button>
        </div>
      );
    }
  };

  const beforeDevicePageNavigation = (): void => {
    localStorage.setItem('focusViewValue', 'patchmanagement');
    localStorage.setItem(
      'search-disabledColumns',
      JSON.stringify([
        'deviceHealth',
        'verified',
        'hostName',
        'labels',
        'bitlocker',
        'lastAccess',
        'tenant',
        'av',
        'alerts',
        'version',
        'lastBootTime',
        'firstSeen',
        'lastLoggedInUser',
        'ipAddresses',
        'osVersion',
        'osName',
        'clientType',
        'geolocation',
        'lastBootTime',
        'firstSeen',
        'clientVersion',
        'rtpState',
        'firewallState',
        'detectedAVName',
        'definitionNumber'
      ])
    );
  };

  const closeCrossLinkAlert = (): void => {
    const newState = { visible: false, lastCloseDate: new Date(), closedTimes: crossLinkInfoBoxStore.closedTimes + 1 };
    setCrossLinkInfoBoxStore(newState);
  };

  return (
    <>
      <div className={styles.buttonContainer} style={{ ...(crossLinkInfoBoxStore.visible ? { top: '4rem' } : {}) }}>
        <TrackedButton
          trackingEvent={new PatchViewUpdatesSelectedEvent(Object.values(rowSelection).length)}
          variant="primary"
          disabled={!Object.values(rowSelection).length}
          onClick={() => {
            navigate({ path: '/schedule' });
          }}>
          {t('windowsUpdatesSelector.installUpdates')}
        </TrackedButton>
      </div>
      <WindowsUpdatesGrid
        data={showAsLoading ? (updates?.map(() => null) ?? [null, null, null]) : updates}
        onPaginationChange={handlePaginationChange}
        pagination={pagination}
        onRowSelectionChange={handleRowSelectionChange}
        rowSelection={rowSelection}
        onSortingChange={handleSortingChange}
        sorting={sorting}
        globalFilterEnabled={true}
        onGlobalFilterChange={(searchString: string) => {
          dispatch(setSearchText(searchString));
          dispatch(setPagination({ ...pagination, pageIndex: 0 }));
        }}
        searchText={searchText}
        state={{ pagination, rowSelection, sorting }}
        isLoading={showAsLoading}
        renderDeselectButton={renderDeselectButton()}
        pageCount={pageCount()}
        totalRowCount={updatesCount}
        tenantDevicesAndGroups={tenantDevicesAndGroups}
        renderHeaderTop={
          crossLinkInfoBoxStore.visible ? (
            <Alert variant="info" icon={<InfoOutlinedIcon />} className={styles.alert}>
              <div className={styles.alertContent}>
                <span>
                  <Trans
                    i18nKey={'windowsUpdatesSelector.alertDescription'}
                    components={{
                      deviceUpdateViewLink: (
                        <GoToResolveAppLink
                          route={{
                            path: '/devices/individual-devices?searchTableSortOrder=:searchTableSortOrder',
                            params: { searchTableSortOrder: JSON.stringify({ updateStatus: 'desc' }) }
                          }}
                          beforeNavigate={beforeDevicePageNavigation}
                        />
                      )
                    }}
                  />
                </span>
                <Button variant="tertiary" onClick={closeCrossLinkAlert}>
                  <CloseIcon />
                </Button>
              </div>
            </Alert>
          ) : (
            <> </>
          )
        }
        renderSlot={
          <>
            <WindowsUpdatesFilterHeader
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setIsFilterPanelOpen={setIsFilterPanelOpen}
              handleShowSecurityUpdatesFilterChange={handleShowSecurityUpdatesFilterChange}
              handleTenantFilterChange={handleTenantFilterChange}
              tenantsAutocompleteRef={tenantsAutocompleteRef}></WindowsUpdatesFilterHeader>
            <WindowsUpdatesFilterPanel
              filtersState={columnFilters}
              isOpen={isFilterPanelOpen}
              onClose={() => setIsFilterPanelOpen(false)}
              setColumnFilters={setColumnFilters}
              showSecurityUpdatesOnly={showSecurityUpdatesOnly}
              handleShowSecurityUpdatesFilterChange={handleShowSecurityUpdatesFilterChange}
              handleTenantFilterChange={handleTenantFilterChange}
              tenantsAutocompleteRef={tenantsAutocompleteRef}
              globalTenants={globalTenants}></WindowsUpdatesFilterPanel>
          </>
        }
        renderTableFooter={
          <>
            {!isUpToDateAndHasDevicesWithNoFilters && !updates?.length && (
              <Typography variant="caption-medium" className={styles.windowsUpdatesTableFooter}>
                {t('windowsUpdatesSelector.noUpdatesToShow')}
              </Typography>
            )}
          </>
        }></WindowsUpdatesGrid>
    </>
  );
}

export default WindowsUpdatesSelector;
